/**
 * Utility functions for waiting and polling session-related operations
 */
import { sleep } from '@trimble/http-utils';

/**
 * Options for waitForCondition polling
 */
export interface WaitForConditionOptions {
  /** Maximum time to wait in milliseconds (default: 2000) */
  maxWaitTimeMs?: number;
  /** Interval between checks in milliseconds (default: 100) */
  checkIntervalMs?: number;
}

/**
 * Polls a condition function until it returns true or timeout is reached.
 * Useful for waiting for master tab to refresh session or other async operations.
 *
 * @param condition Function that returns true (or Promise<true>) when condition is met
 * @param options Wait options (maxWaitTimeMs, checkIntervalMs)
 * @returns Promise that resolves to true if condition was met, false if timeout
 *
 * @example
 * ```typescript
 * const success = await waitForCondition(
 *   async () => {
 *     const session = await service.loadSession();
 *     return session && isValidSession(session);
 *   },
 *   { maxWaitTimeMs: 3000, checkIntervalMs: 100 }
 * );
 * ```
 */
export async function waitForCondition(
  condition: () => boolean | Promise<boolean>,
  options: WaitForConditionOptions = {}
): Promise<boolean> {
  const maxWaitTimeMs = options.maxWaitTimeMs ?? 2000;
  const checkIntervalMs = options.checkIntervalMs ?? 100;
  const waitStartTime = Date.now();

  while (Date.now() - waitStartTime < maxWaitTimeMs) {
    if (await condition()) {
      return true;
    }
    await sleep(checkIntervalMs);
  }

  return false;
}
